import { useTranslations } from "next-intl";
import { useColors } from "@/data/colors";
import { Color } from "@/types/colors";
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useEffect,
} from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@repo/ui/lib/utils";
import { useAutoSubmit } from "@/hooks/useAutoSubmit";

const FormSchema = z.object({
  colorId: z.array(z.string()),
  // .refine((value) => value.some((item) => item), {}),
});

export const FiltersMenuItemColors = ({
  applyFilter,
  initialValue,
}: {
  applyFilter: (data: { colorId: string[] } | null) => void;
  initialValue: string[];
}) => {
  const t = useTranslations("Products-list.filters");
  const { data: colors } = useColors();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      colorId: [],
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    applyFilter(data);
  }

  useEffect(() => {
    form.setValue("colorId", initialValue);
  }, [initialValue]);

  useAutoSubmit({
    trigger: form.trigger,
    watch: form.watch,
    onSubmit: form.handleSubmit(onSubmit),
  });

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <div className="py-[16px]">
          <AccordionTrigger className="max-md:text-base font-medium py-0 data-[state=open]:text-primary-magenta">
            {t("color")}
          </AccordionTrigger>
          <AccordionContent className="mt-[20px] p-0">
            <div className="pb-6 text-left w-full">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-8"
                >
                  <FormField
                    control={form.control}
                    name="colorId"
                    render={() => (
                      <FormItem>
                        <div className="flex flex-col items-start">
                          {colors &&
                            colors.map((item: Color) => (
                              <FormField
                                key={item.id}
                                control={form.control}
                                name="colorId"
                                render={({ field }) => {
                                  return (
                                    <FormItem key={item.id} className="w-full">
                                      <FormControl>
                                        <FilterMenuItemColorsCheckbox
                                          color={item}
                                          checked={field.value?.includes(
                                            item.id,
                                          )}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([
                                                  ...field.value,
                                                  item.id,
                                                ])
                                              : field.onChange(
                                                  field.value?.filter(
                                                    (value) =>
                                                      value !== item.id,
                                                  ),
                                                );
                                          }}
                                        ></FilterMenuItemColorsCheckbox>
                                      </FormControl>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </form>
              </Form>
            </div>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
};

type FilterMenuItemColorsCheckboxProps = {
  color: Color;
} & Omit<ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "color">;

const FilterMenuItemColorsCheckbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  FilterMenuItemColorsCheckboxProps
>(({ className, color, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn("w-full py-2 flex gap-4 items-center", {
        "font-bold": props.checked,
      })}
      {...props}
    >
      <div
        className={cn(
          "ml-2 rounded-full h-[24px] w-[24px] border-secondary-gray4 border-[1.5px]",
          { "ring-2 ring-offset-[3px] ring-primary-magenta": props.checked },
        )}
        style={{ backgroundColor: color?.hex }}
      />
      <span className={cn("flex-1 text-left", { "font-bold": props.checked })}>
        {color?.name}
      </span>
    </CheckboxPrimitive.Root>
  );
});
